import axios from 'axios'
import Product from '~/js/models/product'

const DEFAULT_GRAPHQL_ENDPOINT = '/api/unstable/graphql.json'
const DEFAULT_METADATA_PAGE_SIZE = 250
const DEFAULT_PRODUCT_PAGE_SIZE = 20
const CACHE_VERSION = 1
const CACHE_TTL = 5 * 60 * 1000
const COLLECTION_PRODUCTS_CACHE_KEY = 'collection-visible-product-ids'

const COLLECTION_PRODUCT_METADATA_FIELDS = `
  id
  availableForSale
  tags
`

const COLLECTION_PRODUCT_FIELDS = `
  id
  availableForSale
  compareAtPriceRange {
    minVariantPrice {
      amount
    }
  }
  featuredImage {
    url
  }
  handle
  productType
  priceRange {
    minVariantPrice {
      amount
    }
  }
  tags
  title
  totalInventory
  vendor
  selectedOrFirstAvailableVariant {
    id
    availableForSale
    compareAtPrice {
      amount
    }
    price {
      amount
    }
    sku
  }
`

const buildCollectionProductsQuery = ({ fields, pageSize }) => {
  return `
    query getCollectionProducts($id: ID!, $cursor: String) {
      collection(id: $id) {
        products(first: ${pageSize}, after: $cursor) {
          edges {
            node {
              ${fields}
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `
}

const createEmptyCollectionProductIndex = () => {
  return {
    completed: false,
    cursor: null,
    hasNextPage: true,
    ids: [],
    updatedAt: Date.now(),
    version: CACHE_VERSION,
  }
}

const buildCollectionProductIndexCacheKey = (collectionId) => {
  return `${COLLECTION_PRODUCTS_CACHE_KEY}:${collectionId}`
}

const readCollectionProductIndex = (collectionId) => {
  try {
    const rawValue = sessionStorage.getItem(
      buildCollectionProductIndexCacheKey(collectionId)
    )
    if (!rawValue) return createEmptyCollectionProductIndex()

    const parsed = JSON.parse(rawValue)
    if (
      parsed?.version !== CACHE_VERSION ||
      !Array.isArray(parsed.ids) ||
      Date.now() - Number(parsed.updatedAt || 0) > CACHE_TTL
    ) {
      return createEmptyCollectionProductIndex()
    }

    return {
      completed: Boolean(parsed.completed),
      cursor: parsed.cursor || null,
      hasNextPage:
        parsed.hasNextPage === undefined
          ? !parsed.completed
          : parsed.hasNextPage,
      ids: parsed.ids,
      updatedAt: Number(parsed.updatedAt || Date.now()),
      version: CACHE_VERSION,
    }
  } catch (error) {
    return createEmptyCollectionProductIndex()
  }
}

const writeCollectionProductIndex = (collectionId, state) => {
  try {
    sessionStorage.setItem(
      buildCollectionProductIndexCacheKey(collectionId),
      JSON.stringify(state)
    )
  } catch (error) {
    // Ignore cache write failures and continue with in-memory state.
  }
}

export const normalizeStorefrontCollectionProduct = (node) => {
  const variant = node.selectedOrFirstAvailableVariant
  const variantId = Product.getNumericIdFromGid(variant?.id)
  const inventoryQuantity = node.totalInventory ?? undefined

  return new Product({
    available: variant?.availableForSale ?? node.availableForSale,
    compare_at_price: Product.toProductPriceUnit(
      variant?.compareAtPrice?.amount
    ),
    compare_at_price_min: Product.toProductPriceUnit(
      node.compareAtPriceRange?.minVariantPrice?.amount
    ),
    featured_image: node.featuredImage?.url || '',
    handle: node.handle,
    id: Product.getNumericIdFromGid(node.id),
    inventory_quantity: inventoryQuantity,
    price: Product.toProductPriceUnit(variant?.price?.amount),
    price_min: Product.toProductPriceUnit(
      node.priceRange?.minVariantPrice?.amount
    ),
    sku: variant?.sku || node.handle,
    tags: node.tags || [],
    title: node.title,
    type: node.productType || '',
    url: `/products/${node.handle}`,
    variants: variantId ? [{ id: variantId }] : [],
    vendor: node.vendor,
  })
}

export const isVisibleStorefrontCollectionProduct = (node) => {
  return (
    node.availableForSale &&
    !node.tags?.includes('algolia-ignore') &&
    !node.tags?.includes('out_of_stock')
  )
}

const fetchCollectionProductsConnection = async ({
  collectionId,
  storefrontAccessToken,
  cursor,
  fields,
  pageSize,
  graphqlEndpoint = DEFAULT_GRAPHQL_ENDPOINT,
}) => {
  const collectionGid = `gid://shopify/Collection/${collectionId}`
  const { data } = await axios.post(
    graphqlEndpoint,
    {
      query: buildCollectionProductsQuery({ fields, pageSize }),
      variables: {
        id: collectionGid,
        cursor,
      },
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
    }
  )

  return data?.data?.collection?.products || null
}

export const getCachedCollectionProductTotal = (state) => {
  return state.ids.length
}

export const ensureCollectionProductIndex = async ({
  collectionId,
  storefrontAccessToken,
  minimumVisibleCount = 0,
  shouldComplete = false,
  onProgress,
  pageSize = DEFAULT_METADATA_PAGE_SIZE,
  graphqlEndpoint = DEFAULT_GRAPHQL_ENDPOINT,
}) => {
  const state = readCollectionProductIndex(collectionId)

  while (
    state.hasNextPage &&
    (shouldComplete || state.ids.length < minimumVisibleCount)
  ) {
    const connection = await fetchCollectionProductsConnection({
      collectionId,
      storefrontAccessToken,
      cursor: state.cursor,
      fields: COLLECTION_PRODUCT_METADATA_FIELDS,
      pageSize,
      graphqlEndpoint,
    })
    if (!connection) {
      state.hasNextPage = false
      state.completed = true
      break
    }

    const visibleIds = connection.edges
      .map((edge) => edge.node)
      .filter(isVisibleStorefrontCollectionProduct)
      .map((node) => Product.getNumericIdFromGid(node.id))
      .filter((id) => id !== undefined)

    state.ids.push(...visibleIds)
    state.cursor = connection.pageInfo?.endCursor || null
    state.hasNextPage = Boolean(connection.pageInfo?.hasNextPage)
    state.completed = !state.hasNextPage
    state.updatedAt = Date.now()

    writeCollectionProductIndex(collectionId, state)

    if (onProgress) onProgress({ ...state })
  }

  state.updatedAt = Date.now()
  writeCollectionProductIndex(collectionId, state)

  return state
}

export const fetchVisibleCollectionProducts = async ({
  collectionId,
  storefrontAccessToken,
  limit = Number.POSITIVE_INFINITY,
  pageSize = DEFAULT_PRODUCT_PAGE_SIZE,
  graphqlEndpoint = DEFAULT_GRAPHQL_ENDPOINT,
}) => {
  const products = []
  let cursor = null
  let hasNextPage = true

  while (hasNextPage && products.length < limit) {
    const connection = await fetchCollectionProductsConnection({
      collectionId,
      storefrontAccessToken,
      cursor,
      fields: COLLECTION_PRODUCT_FIELDS,
      pageSize,
      graphqlEndpoint,
    })
    if (!connection) break

    const visibleProducts = connection.edges
      .map((edge) => edge.node)
      .filter(isVisibleStorefrontCollectionProduct)
      .map(normalizeStorefrontCollectionProduct)

    products.push(...visibleProducts)
    hasNextPage = Boolean(connection.pageInfo?.hasNextPage)
    cursor = connection.pageInfo?.endCursor || null
  }

  return products.slice(0, limit)
}
